"use client";

import Session from "@lib/session";
import { getContent } from "@lib/use_content";
import loginProfile from "@services/login_profile";
import logoutProfile from "@services/logout_profile";

interface UpdateFromUrlResponse {
  redirectTo?: string;
  error?: string;
}

// Manage configuration coming down from the CMS Plugin via URL params.
export default class FrameConfig implements be.FrameConfig {
  bookingUrl?: string;
  bridgeId?: number;
  bridgeType?: number;
  groupCode?: string;
  jsonHeaders?: Record<string, string>;
  language?: string = "en";
  mode?: string; // ex. clientcenter
  override?: string = "";
  lockGroupProfile?: boolean;
  parentUrl?: string;
  resetPasswordUrl?: string;
  serviceArea?: number;
  serviceAreaName?: string;
  tokenGroup?: string = "BookingEngine";

  constructor() {
    if (!global.sessionStorage) return;

    let config = sessionStorage.getItem(`${window.siteId}.frameConfig`);
    let configObj: be.FrameConfig;

    if (config === null) return;

    try {
      configObj = JSON.parse(config);
    } catch (e) {
      return;
    }

    if (configObj) {
      this.groupCode = configObj.groupCode;
      if (configObj.language) this.language = configObj.language;
      window.language = this.language || "en";
      if (configObj.override) this.override = configObj.override;
      if (configObj.jsonHeaders) this.jsonHeaders = configObj.jsonHeaders;
      if (configObj.bridgeId !== undefined) this.bridgeId = configObj.bridgeId;
      if (configObj.bridgeType !== undefined) this.bridgeType = configObj.bridgeType;

      this.bookingUrl = configObj.bookingUrl;
      this.lockGroupProfile = configObj.lockGroupProfile;
      this.mode = configObj.mode;
      this.parentUrl = configObj.parentUrl;
      this.resetPasswordUrl = configObj.resetPasswordUrl;
      this.serviceArea = configObj.serviceArea;
      this.serviceAreaName = configObj.serviceAreaName;
      if (configObj.tokenGroup) this.tokenGroup = configObj.tokenGroup;
    }
  }

  // Apply frame config to the booking data. Generally called after clearing the session.
  apply() {
    this.applyServiceArea();
  }

  async applyServiceArea() {
    if (!this.serviceArea) return;
    Session.updateConfig({
      serviceArea: this.serviceArea,
    });
  }

  async clearCodes() {
    await logoutProfile("GROUP");
    await logoutProfile("SALES");
    await Session.clearSession();
    await global.window.apolloClient.resetStore();
  }

  save() {
    if (!global.sessionStorage) return null;
    sessionStorage.setItem(`${window.siteId}.frameConfig`, JSON.stringify(this));
    window.dispatchEvent(new CustomEvent("frameConfigChange"));
  }

  async setGroupCode(groupCode: string) {
    if (groupCode == this.groupCode) return;

    // Clean up group code.
    groupCode = groupCode.replace(/^[^a-z\d]*|[^a-z\d]*$/gi, "");

    await Session.clearSession();
    const { actualType, error, success } = await loginProfile({ id: groupCode, type: "GROUP" });

    if (success) {
      this.groupCode = groupCode;
      this.save();
      await global.window.apolloClient.resetStore();
      return { error: false, success: true, profileType: actualType };
    } else {
      this.groupCode = undefined;
      this.save();
      await global.window.apolloClient.resetStore();
      return { error };
    }
  }

  async setMode(mode: string) {
    if (mode == this.mode) return;
    this.mode = mode;
    this.save();
    await Session.clearSession();
    await global.window.apolloClient.resetStore();
  }

  async setServiceArea(serviceArea: number | undefined) {
    if (serviceArea == this.serviceArea) return;
    // getContent REJECTS on a load failure (Apollo's default errorPolicy "none");
    // the area name is analytics-only, so a failed read must not abort the area
    // switch.
    let serviceAreaName: string | undefined;
    try {
      const { data: content } = await getContent(["serviceAreaName"], serviceArea);
      serviceAreaName = content?.content?.serviceAreaName ?? undefined;
    } catch (e) {
      console.error("Failed to load service-area name content:", e);
    }

    Session.clearReservationData();
    this.serviceArea = serviceArea;
    this.serviceAreaName = serviceAreaName;
    await this.applyServiceArea();
    this.save();
  }

  update(name: keyof be.FrameConfig, value: any) {
    this[name] = value;
    this.save();
  }

  async updateFromURL(query: Object): Promise<UpdateFromUrlResponse | undefined> {
    let action = undefined;

    // Booking URL
    if (query["attached_config[main_frame_page]"]) {
      this.bookingUrl = query["attached_config[main_frame_page]"].toString();
      // Fix weird bug- double backslashes are getting replaced with single backslashes somewhere.
      this.bookingUrl = this.bookingUrl.replace(/https?:\/(\w)/, "https://$1");
      this.save();
    }

    // Current parent URL
    if (query["ac[current_parent_url]"]) {
      this.parentUrl = query["ac[current_parent_url]"].toString();
      this.save();
    }

    // Reset password URL
    if (query["ac[reset_password_url]"]) {
      this.resetPasswordUrl = query["ac[reset_password_url]"].toString();
      // Fix weird bug- double backslashes are getting replaced with single backslashes somewhere.
      this.resetPasswordUrl = this.resetPasswordUrl.replace(/https?:\/(\w)/, "https://$1");
      this.save();
    }

    // Language and override are only SAVED here, never followed by an Apollo store
    // reset: save() dispatches frameConfigChange, and ApolloProvider rebuilds the
    // client — fresh cache, new request headers — whenever the saved value changes.
    // A reset on the outgoing client would only kill its in-flight queries (Apollo
    // "Store reset while query was in flight", error #34).

    // Language
    const language = query["locale"] || query["lang"] || this.language || "en";
    this.language = language.toString().toLowerCase();
    window.language = this.language;
    this.save();

    // Overrides
    if (query.hasOwnProperty("or")) this.override = query["or"];
    else this.override = this.override || "";
    this.save();

    // JSON Headers
    const headerObj = {};
    Object.entries(query).forEach(([key, value]) => {
      if (key.startsWith("header_")) {
        let headerKey = key.replace("header_", "");
        headerObj[headerKey] = value;
      }
    });
    if (Object.keys(headerObj).length != 0) this.jsonHeaders = headerObj;
    else this.jsonHeaders = this.jsonHeaders || {};
    this.save();

    // Bridge routing params
    const currentBridgeId = this.bridgeId;
    const currentBridgeType = this.bridgeType;
    if (query.hasOwnProperty("bridge_id")) {
      this.bridgeId = query["bridge_id"] ? parseInt(query["bridge_id"] as string, 10) : undefined;
    }
    if (query.hasOwnProperty("bridge_type")) {
      this.bridgeType = query["bridge_type"] ? parseInt(query["bridge_type"] as string, 10) : undefined;
    }
    if (currentBridgeId !== this.bridgeId || currentBridgeType !== this.bridgeType) {
      this.save();
      await global.window.apolloClient.resetStore();
    }

    // Application mode (ex. clientcenter)
    if (query["mode"]) this.setMode(query["mode"].toString());

    // Service area
    if (query["ac[service_area]"] || query["trip[service_area]"]) {
      let serviceArea = query["ac[service_area]"] || query["trip[service_area]"];
      await this.setServiceArea(parseInt(serviceArea as string));
    }

    // Lock group profile
    if (query["ac[lock_group_profile]"]) {
      this.lockGroupProfile = !!Number(query["ac[lock_group_profile]"]);
      this.save();
    }

    // Group code
    if (query["code"]) {
      let returnVal = await this.setGroupCode(query["code"] as string);
      if (returnVal?.error == "PASSWORD_NEEDED") {
        action = { redirectTo: `group_password?code=${query.code}` };
      } else if (returnVal?.error) {
        action = { error: returnVal?.error };
      }
    }

    // Detach codes
    if (query.detach_all_codes) {
      await this.clearCodes();
    }

    return action;
  }
}
